import React from 'react';

import { useMutation } from '@apollo/react-hooks';
import SpinnerButton from '@do/walrus/SpinnerButton';
import TextInput from '@do/walrus/TextInput';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import * as Yup from 'yup';
import { createClient } from '../client';
import {
  EMAIL_MAX_LEN,
  LOGIN_REDIRECT_URL,
  REGISTER_ENDPOINT
} from '../constants';
import copy from '../copy';
import { CREATE_ACCOUNT } from '../queries/queries';
import { handleFormError } from '../utils/gql-utils';
import { yupPasswordValidation } from '../utils/validation-utils';
import { ErrorBanner } from './Common';

const formCopy = copy.form;

const FormValidation = Yup.object().shape({
  email: Yup.string()
    .email(formCopy.validation.email_valid)
    .required(formCopy.validation.email_required)
    .max(EMAIL_MAX_LEN, copy.errors.too_long.details),
  password: yupPasswordValidation
});

interface CreateGqlResponse {
  createAccount: {
    uuid: string;
    email: string;
    redirect_url?: string;
  };
}

interface CreateGqlVariables {
  createReq: {
    name?: string;
    email: string;
    password: string;
    onboarding_origin?: string;
  };
}

interface Props {
  email?: string;
  onboardingOrigin?: string;
}
const client = createClient(REGISTER_ENDPOINT);

const RegistrationForm: React.FC<Props> = ({ email, onboardingOrigin }) => {
  const [createAccount, { loading }] = useMutation<
    CreateGqlResponse,
    CreateGqlVariables
  >(CREATE_ACCOUNT, { client });

  const [genericServerError, setGenericServerError] = React.useState(null);
  const [redirecting, setRedirecting] = React.useState(false);

  return (
    <Formik
      initialValues={{
        name: '',
        password: '',
        email: email || '',
        onboarding_origin: onboardingOrigin || ''
      }}
      validationSchema={FormValidation}
      onSubmit={(values, { setErrors }) => {
        setGenericServerError(null); /* Reset displayed error */
        createAccount({
          variables: {
            createReq: values
          }
        })
          .then((successResponse) => {
            const redirect =
              _get(successResponse, 'data.createAccount.redirect_url') ||
              LOGIN_REDIRECT_URL;

            window.location.assign(redirect);
            setRedirecting(true);
          })
          .catch(handleFormError(setErrors, setGenericServerError));
      }}
      render={({ errors, values, touched }) => (
        <Form autoComplete="none">
          {genericServerError && <ErrorBanner error={genericServerError} />}

          <TextInput
            placeholder={formCopy.name_placeholder}
            name="name"
            label={formCopy.name_label}
            className="do-u-mb--small"
            touched={touched.name}
            error={errors.name}
            value={values.name}
            autoComplete="none"
            autoFocus
          />

          <TextInput
            placeholder={formCopy.email_placeholder}
            required
            name="email"
            label={formCopy.email_label}
            className="do-u-mb--small"
            touched={touched.email}
            error={errors.email}
            value={values.email}
            autoComplete="none"
          />

          <TextInput
            placeholder={formCopy.register.password_placeholder}
            required
            name="password"
            label={formCopy.password_label}
            type="password"
            className="do-u-mb--medium"
            touched={touched.password}
            error={errors.password}
            value={values.password}
            autoComplete="none"
          />

          <SpinnerButton
            type="submit"
            variation="success"
            fullWidth={true}
            disabled={loading || redirecting}
            spinning={loading || redirecting}
            loadingMessage={copy.loading.registration.belowButton}
          >
            {formCopy.register.button_text}
          </SpinnerButton>
        </Form>
      )}
    />
  );
};

export default RegistrationForm;
